'use client'

import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { UserCard } from '../hooks/useUserCards'

interface SelectedCardContextValue {
  selectedCard: UserCard | undefined
  selectedCardId: string | undefined
  setSelectedCardId: (id: string) => void
  cards: UserCard[]
}

const SelectedCardContext = createContext<SelectedCardContextValue | null>(null)

interface SelectedCardProviderProps {
  cards: UserCard[]
  children: ReactNode
}

export function SelectedCardProvider({ cards, children }: SelectedCardProviderProps) {
  const [overrideCardId, setOverrideCardId] = useState<string | null>(null)

  const defaultCard = useMemo(() => {
    return cards[0]
  }, [cards])

  useEffect(() => {
    if (overrideCardId && cards.length > 0 && !cards.some((c) => c.cardID === overrideCardId)) {
      setOverrideCardId(null)
    }
  }, [cards, overrideCardId])

  const selectedCardId = overrideCardId ?? defaultCard?.cardID
  const selectedCard = useMemo(() => {
    if (!selectedCardId) return undefined
    return cards.find((c) => c.cardID === selectedCardId) ?? defaultCard
  }, [cards, selectedCardId, defaultCard])

  const setSelectedCardId = useCallback((id: string) => {
    setOverrideCardId(id)
  }, [])

  const value = useMemo<SelectedCardContextValue>(
    () => ({ selectedCard, selectedCardId, setSelectedCardId, cards }),
    [selectedCard, selectedCardId, setSelectedCardId, cards],
  )

  return <SelectedCardContext.Provider value={value}>{children}</SelectedCardContext.Provider>
}

export function useSelectedCard(): SelectedCardContextValue {
  const ctx = useContext(SelectedCardContext)
  if (!ctx) {
    throw new Error('useSelectedCard must be used within a SelectedCardProvider')
  }
  return ctx
}
